import React from "react";
import View from "../../components/ReactNative/View";
import Text from "../../components/ReactNative/Text";
import { useSpecificSize } from "../../../specific/Theme/SpecificSize";
import i18n, { translate } from "../../utils/Localization/Localization";
import Colors from "../../Theme/Colors";
import { Image, ImageStyle, Platform, ScrollView, TextStyle, TouchableOpacity } from "react-native";
import NavBarTitle from "../../components/NavBar/NavBarTitle";
import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";
import AppTheme from "../../utils/Theme";
import User from "../../data/user/User";
import ActuSection from "../../components/Actu/ActuSection";
import SectionHeader from "../../components/Rooms/SectionHeader";
import Fonts from "../../Theme/Fonts";
import { KEYS } from "../../components/Rooms/SuperRoom";
import * as Navigation from "../../../specific/utils/Navigation/Navigation";
import { useNavigate } from "../../../specific/utils/Navigation/Navigation";
import * as Constants from "../../utils/Constants";
import { analyticsEventHpWellBeingResourcesClicked, analyticsEventSectionTrainingCPSClicked, analyticsEventSectionTrainingPhiloClicked, trackEvent } from "../../services/Tracking/Analytics";
import Divider from "../../designSystem/Divider/Divider";

interface InstitutionRoomProps {
    componentId: string;
}

// Différenciation avec la salle des parents. Cette page s'adresse dans un premier temps aux profs et animateurs et à toutes nos cibles ayant une page construite de la même manière
const InstitutionRoom = (props: InstitutionRoomProps) => {

    const { componentId } = props;
    const appTheme: AppTheme = new AppTheme();
    const user: User = User.getInstance();

    let navigate = null;
    if (Platform.OS === "web") {
        navigate = useNavigate()
    }

    const {
        fullScreenWidth,
        fullScreenHeight,
        availableScreenSize,
        contentSize,
        sideMenuSize,
        horizontalSpacing,
        pixelRatio
    } = useSpecificSize();

    const sectionsContainerWidth: number = Platform.OS === "web" ? contentSize * 3 / 2 : contentSize - 2 * horizontalSpacing;
    const sectionsWidth: number = Platform.OS === "web" ? sectionsContainerWidth * 0.9 : sectionsContainerWidth - 2 * horizontalSpacing;
    const semiSectionWidth: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? (sectionsWidth - horizontalSpacing)/2 : sectionsWidth;

    const sectionTitleStyle: TextStyle = {
        fontFamily: appTheme.superHerosFontBold,
        fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(13) : appTheme.pixelPerfectForFont(12),
        color: appTheme.darkBlue,
        textAlign: "center",
        marginTop: 20,
        paddingHorizontal: 10
    }

    const sectionBodyStyle: TextStyle = {
        fontFamily: appTheme.primarySemiBoldFont,
        fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(8) : appTheme.pixelPerfectForFont(11),
        color: appTheme.darkBlue,
        paddingHorizontal: appTheme.pixelPerfect(40),
        textAlign: "center"
    }

    const sectionImageStyle: ImageStyle = {
        width: sectionsWidth,
        height: Platform.OS === "web" ? sectionsWidth * 417 / 2067 : sectionsWidth * 7 / 16
    }

    const semiSectionImageStyle: ImageStyle = {
        width: semiSectionWidth,
        height: semiSectionWidth * 7 / 16
    }

    const renderHeader = (): React.JSX.Element => {
        const imageSource: number = user.profile === "teacher" ? Platform.OS === "web" ? require("../../assets/img/rooms/teacher/header_web.png") : require("../../assets/img/rooms/teacher/header_mobile.png") : Platform.OS === "web" ? require("../../assets/img/rooms/animator/header_web.png") : require("../../assets/img/rooms/animator/header_mobile.png");
        const imageRatio: number = Platform.OS === "web" ? 729 / 4000 : 926 / 1920;
        const imageWidth: number = availableScreenSize;
        const imageHeight: number = imageWidth * imageRatio;
        const paddingTop: number = Platform.OS === "web" ? 40 : 0;
        return <View style={{ paddingTop }}>
            <Image source={imageSource} style={{ width: imageWidth, height: imageHeight }} />
        </View>
    }

    const launchSection = (key: string) => {
        if (key === KEYS.wellBeing) {
            trackEvent(analyticsEventHpWellBeingResourcesClicked);
        } else if (key === KEYS.philo) {
            trackEvent(analyticsEventSectionTrainingPhiloClicked);
        } else if (key === KEYS.cps) {
            trackEvent(analyticsEventSectionTrainingCPSClicked);
        }
        Navigation.push({
            componentId,
            navigate,
            name: Constants.ScreenCategoryHome,
            passProps: {
                categoryKey: key,
                section: "room",
                origin: "menu"
            }
        });
    }

    const renderNeuroScienceSection = () => {
        const marginEnd: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : 0;
        let headerNeuroScienceImage = user.profile === "teacher" ? require("../../assets/img/rooms/ill_lae_neurosciences.png") : require("../../assets/img/rooms/ill_lep_neurosciences.png");
        if (Platform.OS === "web") {
            headerNeuroScienceImage = user.profile === "teacher" ? require("../../assets/img/rooms/ill_lae_neurosciences_web.png") : require("../../assets/img/rooms/ill_lep_neurosciences_web.png");
        }
        return <View style={{ marginEnd, alignItems: "center", justifyContent: "center", width: sectionsWidth, flexDirection: "row" }}>
            <View style={{ alignItems: "center", justifyContent: "flex-start", width: sectionsWidth, paddingTop: appTheme.pixelPerfect(20) }}>
                <TouchableOpacity onPress={() => launchSection(KEYS.cps)} style={{ paddingBottom: appTheme.pixelPerfect(20), width: sectionsWidth, backgroundColor: "#736EA6", borderRadius: appTheme.pixelPerfect(30), alignItems: "center", overflow: "hidden" }}>
                    <Image source={headerNeuroScienceImage} style={sectionImageStyle} />
                    <Text style={[sectionTitleStyle, { color: "#FFFFFF" }]}>{translate.labels().rooms.auto_trainings.cps.title}</Text>
                    <Divider />
                    <Text style={[sectionBodyStyle , { color: "#FFFFFF" }]}>{translate.labels().rooms.auto_trainings.cps.body}</Text>
                </TouchableOpacity>
            </View>
        </View>
    }

    const renderCPSSection = () => {
        const marginEnd: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : 0;
        const headerCPSImage: number = Platform.OS === "web" ? require("../../assets/img/cat_cps_web.png") : require("../../assets/img/cat_cps.png");
        return <View style={{ marginEnd, alignItems: "center", justifyContent: "center", width: sectionsWidth, flexDirection: "row" }}>
            <View style={{ alignItems: "center", justifyContent: "flex-start", width: sectionsWidth, paddingTop: appTheme.pixelPerfect(20) }}>
                <TouchableOpacity onPress={() => launchSection(KEYS.cps)} style={{ paddingBottom: appTheme.pixelPerfect(20), width: sectionsWidth, backgroundColor: appTheme.cps, borderRadius: appTheme.pixelPerfect(30), alignItems: "center", overflow: "hidden" }}>
                    <Image source={headerCPSImage} style={sectionImageStyle} />
                    <Text style={sectionTitleStyle}>{translate.labels().rooms.auto_trainings.cps.title}</Text>
                    <Divider />
                    <Text style={sectionBodyStyle}>{translate.labels().rooms.auto_trainings.cps.body}</Text>
                </TouchableOpacity>
            </View>
        </View>
    }

    const renderPhiloSection = () => {
        if ((user.profile === "teacher") || (user.profile === "animator")) {
            const marginEnd: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : 0;
            let headerPhiloImage = user.profile === "teacher" ? require("../../assets/img/rooms/ill_lae_auto_formation_philo.png") : require("../../assets/img/rooms/ill_lep_auto_formation_philo.png");
            if (Platform.OS === "web") {
                headerPhiloImage = user.profile === "teacher" ? require("../../assets/img/rooms/ill_lae_auto_formation_philo_web.png") : require("../../assets/img/rooms/ill_lep_auto_formation_philo_web.png");
            }
            return <View style={{ marginEnd, alignItems: "center", justifyContent: "center", width: sectionsWidth, flexDirection: "row" }}>
                <View style={{ alignItems: "center", justifyContent: "flex-start", width: sectionsWidth, paddingTop: appTheme.pixelPerfect(20) }}>
                    <TouchableOpacity onPress={() => launchSection(KEYS.philo)} style={{ paddingBottom: appTheme.pixelPerfect(20), width: sectionsWidth, backgroundColor: "#5DD9B5", borderRadius: appTheme.pixelPerfect(30), alignItems: "center", overflow: "hidden" }}>
                        <Image source={headerPhiloImage} style={sectionImageStyle} />
                        <Text style={sectionTitleStyle}>{translate.labels().rooms.auto_trainings.philo.title}</Text>
                        <Divider />
                        <Text style={sectionBodyStyle}>{translate.labels().rooms.auto_trainings.philo.body}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        }
        return <View />
    }

    const renderRelaxationSection = () => {
        return <View style={{ alignItems: "center", justifyContent: "center", width: semiSectionWidth, flexDirection: "row" }}>
            <View style={{ alignItems: "center", justifyContent: "flex-start", width: semiSectionWidth, paddingTop: appTheme.pixelPerfect(10) }}>
                <TouchableOpacity onPress={() => launchSection(KEYS.wellBeing)} style={{ paddingBottom: appTheme.pixelPerfect(20), width: semiSectionWidth, backgroundColor: "#A2F2F8", borderRadius: appTheme.pixelPerfect(30), alignItems: "center", overflow: "hidden" }}>
                    <Image source={require("../../assets/img/rooms/ill_bien_etre.png")} style={semiSectionImageStyle} />
                    <Text style={sectionTitleStyle}>{translate.labels().rooms.auto_trainings.well_being[user.profile].title}</Text>
                    <Divider />
                    <Text style={sectionBodyStyle}>{translate.labels().rooms.auto_trainings.well_being[user.profile].body}</Text>
                </TouchableOpacity>
            </View>
        </View>
    }

    const renderParentsAndNeurosciences = () => {
        return <View style={{ alignItems: "center", justifyContent: "center", width: semiSectionWidth, flexDirection: "row" }}>
            <View style={{ alignItems: "center", justifyContent: "flex-start", width: semiSectionWidth, paddingTop: appTheme.pixelPerfect(10) }}>
                <TouchableOpacity onPress={() => launchSection(KEYS.wellBeing)} style={{ paddingBottom: appTheme.pixelPerfect(20), width: semiSectionWidth, backgroundColor: appTheme.darkBlue, borderRadius: appTheme.pixelPerfect(30), alignItems: "center", overflow: "hidden" }}>
                    <Image source={require("../../assets/img/rooms/ill_lae__lep_parnets_neurosciences.png")} style={semiSectionImageStyle} />
                    <Text style={[sectionTitleStyle, { color: "#FFFFFF" }]}>{"PARENTALITÉ ET NEUROSCIENCES"}</Text>
                    <Divider />
                    <Text style={[sectionBodyStyle, { color: "#FFFFFF" }]}>{"Et si comprendre le cerveau des enfants changeait tout... Lili coache les parents au quotidien grâce aux neurosciences, avec des conseils en psychologie pour enfants."}</Text>
                </TouchableOpacity>
            </View>
        </View>
    }

    const renderTitle = (target: "mobile" | "web"): React.JSX.Element | null => {
        if (((Platform.OS === "web") && (target === "mobile")) || ((Platform.OS !== "web") && (target === "web"))) {
            return null;
        }
        const title: string = user.profile === "teacher" ? i18n.t("teacher_room.title") : i18n.t("animator_room.title");
        if (Platform.OS === "web") {
            return <View style={{ backgroundColor: Colors.institutionColor, position: "fixed" }}>
                <NavBarTitle titleColor="#FFFFFF" showLevel={false} title={title} />
            </View>
        }
        return <View style={{ backgroundColor: Colors.institutionColor, paddingTop: appTheme.topInsets }}>
            <NavBarTitle titleColor="#ffffff" showLevel={false} title={title} />
        </View>
    }

    const renderContent = (): React.JSX.Element => {
        return <View style={{ paddingBottom: appTheme.pixelPerfect(100), backgroundColor: Colors.teacherBackgroundColor }}>
            {renderHeader()}
            <View key={"actu"} style={{ width: availableScreenSize, alignItems: "center" }}>
                <ActuSection forcedWidth={appTheme.getFlexDirectionForSplitScreen() === "row" ? (contentSize - 2 * horizontalSpacing - appTheme.pixelPerfect(60)) : (contentSize - 2 * horizontalSpacing - appTheme.pixelPerfect(20))} title={""} />
            </View>
            <View key={"step"} style={{ width: availableScreenSize, alignItems: "center", marginTop: appTheme.pixelPerfect(20) }}>
                <View style={{ flex: 1, paddingHorizontal: 10, width: contentSize, alignItems: "center" }}>
                    <Text style={[{ fontFamily: Fonts.primary.bold, textAlign: "center", color: Colors.darkBlue, fontSize: appTheme.pixelPerfectForFont(9), textTransform: "uppercase" }]}>{"3 étapes pour faire grandir vos élèves en confiance"}</Text>
                </View>
                <View key={"section_one"} style={{ marginTop: appTheme.pixelPerfect(10) }}>
                    <SectionHeader forceWidth={sectionsContainerWidth} number={1} title={"Développez vos compétences avec l'appui de la recherche"} />
                    <View style={{ paddingTop: appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : appTheme.pixelPerfect(10), alignItems: "center", backgroundColor: "#FFFFFF", paddingBottom: appTheme.pixelPerfect(20), borderBottomEndRadius: appTheme.pixelPerfect(14), borderBottomStartRadius: appTheme.pixelPerfect(14) }}>
                        {renderNeuroScienceSection()}
                        {renderCPSSection()}
                        {renderPhiloSection()}
                    </View>
                </View>
                <View key={"section_two"} style={{ marginTop: appTheme.pixelPerfect(10) }}>
                    <SectionHeader forceWidth={sectionsContainerWidth} number={2} title={"CULTIVEZ VOTRE BIEN-ÊTRE ET LA CO-ÉDUCATION AVEC LES PARENTS"} />
                    <View style={{ paddingTop: appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : appTheme.pixelPerfect(10), backgroundColor: "#FFFFFF",  paddingBottom: appTheme.pixelPerfect(20), borderBottomEndRadius: appTheme.pixelPerfect(14), borderBottomStartRadius: appTheme.pixelPerfect(14), flexDirection: appTheme.getFlexDirectionForSplitScreen(), alignItems:"center", justifyContent:"space-evenly" }}>
                        {renderRelaxationSection()}
                        {renderParentsAndNeurosciences()}
                    </View>
                </View>
                <View key={"section_three"} style={{ marginTop: appTheme.pixelPerfect(10) }}>
                    <SectionHeader forceWidth={sectionsContainerWidth} number={3} title={"AMÉLIOREZ LE CLIMAT SCOLAIRE ET AIDEZ VOS ÉLÈVES À GRANDIR EN CONFIANCE"} />
                    <View style={{ paddingTop: appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(20) : appTheme.pixelPerfect(10), backgroundColor: "#FFFFFF",  paddingBottom: appTheme.pixelPerfect(20), borderBottomEndRadius: appTheme.pixelPerfect(14), borderBottomStartRadius: appTheme.pixelPerfect(14) }}>

                    </View>
                </View>
            </View>
        </View>

    }

    const renderContainerWithContent = (): React.JSX.Element => {
        if (Platform.OS === "web") {
            return renderContent();
        }
        return <ScrollView style={{ flex: 1 }}>
            {renderContent()}
        </ScrollView>
    }

    return <View safeArea="bottom" style={{ backgroundColor: Colors.teacherBackgroundColor, flex: 1 }}>
        {renderTitle("mobile")}
        {renderContainerWithContent()}
        {renderTitle("web")}
    </View>
}

export default InstitutionRoom;